import React, {useMemo, useState} from 'react'
import {
  ActionModal,
  Button,
  Icon,
  Loading,
  Modal,
  ToggleSwitch,
} from '@therms/atalaya'
import {
  Alerts,
  RPCDocs,
  RPCs,
  useCurrentUserAnPCheck,
  useLocalStorage,
  useRPC,
  useRPCContext,
  useRPCServerEventHandler,
} from '@therms/react-modules'
import {formatTimeframeFromHours} from '@src/modules/Patrol/utils/formatTimeframe'
import {VisitRun} from '../VisitRun'
import {PatrolRunVisitFormModal} from './components/PatrolRunVisitFormModal'
import {PatrolRouteRunInformation} from '@src/modules/Patrol/router/screens/components/PatrolRouteRunInformation'
import {RunProgressBar} from '@src/modules/Patrol/router/screens/components/RunProgressBar'
import dayjs from 'dayjs'
import {VisitsInProgressWarningModal} from '@src/modules/Patrol/router/screens/components/PatrolRouteRunModal/components/VisitsInProgressWarningModal'
import {MdDeleteForever} from 'react-icons/md'
import VisitStatus = RPCDocs.Patrol.GetPatrolRouteRunDetails.v2.VisitsStatus

type VisitElement = RPCDocs.Patrol.GetPatrolRouteRunDetails.v2.Visit

interface PatrolRouteModalProps {
  closeHandler: () => void
  hasRunEnded?: boolean
  onComplete?: () => void
  patrolRouteRunId?: string
}

function PatrolRouteRunModal({
  closeHandler,
  hasRunEnded,
  onComplete,
  patrolRouteRunId,
}: PatrolRouteModalProps) {
  const {rpcService} = useRPCContext()
  const [isLoading, setIsLoading] = useState(false)
  const [isHideCompletedActive, setIsHideCompletedActive] = useLocalStorage(
    `${hasRunEnded ? 'past' : 'progress'}-ptrl-run-hide-vst`,
    false,
  )
  const [selectedVisit, setSelectedVisit] = useState<VisitElement>()
  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false)
  const [isDeletingModalOpen, setIsDeletingModalOpen] = useState(false)

  const canEditRun = useCurrentUserAnPCheck(
    (user) => user.anp.role.ops.permissions.editPatrolRouteRuns,
  )

  const {call, data, isCalling} = useRPC<
    RPCDocs.Patrol.GetPatrolRouteRunDetails.v2.Args,
    RPCDocs.Patrol.GetPatrolRouteRunDetails.v2.Data
  >(
    RPCs.Patrol.GetPatrolRouteRunDetails.v2,
    patrolRouteRunId ? {patrolRouteRunId} : undefined,
  )

  useRPCServerEventHandler('PatrolRouteRunUpdated', (evt, payload) => {
    if (data?.patrolRouteRun.id === payload.patrolRouteRunId) {
      call()
    }
  })

  const formattedTimeframe = useMemo(() => {
    if (!data) return ''

    return formatTimeframeFromHours({
      timeEnd: data.patrolRouteRun.patrolRoute.timeEnd,
      timeStart: data.patrolRouteRun.patrolRoute.timeStart,
    })
  }, [
    data?.patrolRouteRun.patrolRoute.timeStart,
    data?.patrolRouteRun.patrolRoute.timeEnd,
  ])

  const inProgressVisits = useMemo(() => {
    if (!data?.visits) return []

    return data.visits.filter(
      (visit) => visit.status === VisitStatus.InProgress,
    )
  }, [data?.visits])

  const handleRunEnd = () => {
    if (!patrolRouteRunId) return

    setIsLoading(true)

    rpcService.Patrol.EndPatrolRouteRun.v1
      .call({patrolRouteRunId})
      .then(() => {
        Alerts.success('Patrol Route Ended')
        onComplete?.()
        call()
      })
      .catch((err: any) => {
        Alerts.error('There was a problem saving your visit.', err.message)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const handleReOpenRun = () => {
    if (!patrolRouteRunId) return

    setIsLoading(true)

    rpcService.Patrol.ReOpenPatrolRouteRun.v1
      .call({
        patrolRouteRunId,
      })
      .then(() => {
        Alerts.success('Patrol Route Opened')
        onComplete?.()
        call()
      })
      .catch((err: any) => {
        Alerts.error('There was a problem re opening your visit.', err.message)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const handleDeleteRun = async () => {
    if (!patrolRouteRunId) return

    setIsLoading(true)

    rpcService.Patrol.DeletePatrolRouteRuns.v1
      .call({patrolRouteRunIds: [patrolRouteRunId]})
      .then(() => {
        Alerts.success('Patrol Route Run Deleted')
        onComplete?.()
        closeHandler()
      })
      .catch((err: any) => {
        Alerts.error(
          'There was a problem deleting the patrol route run.',
          err.message,
        )
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  if (!data || (!data && isCalling)) {
    return (
      <Modal escPressClose closeHandler={closeHandler}>
        <div className="p-lg flex items-center justify-center">
          <Loading size="lg" />
        </div>
      </Modal>
    )
  }

  if (!data.patrolRouteRun && !isCalling) {
    return (
      <Modal escPressClose closeHandler={closeHandler}>
        <div className="text-md">Patrol Route Run Not found</div>

        <Button className="mt-base" onClick={call}>
          Retry
        </Button>
      </Modal>
    )
  }

  const {
    endDate,
    patrolRoute,
    startDate,
    startedByUserId,
    status,
    userIds,
    visitsCount,
    visitsDoneCount,
  } = data.patrolRouteRun

  const allVisitsComplete = visitsDoneCount >= visitsCount
  const isInProgress =
    status === RPCDocs.Patrol.GetPatrolRouteRunDetails.v2.Status.Inprogress
  const allowReOpen =
    !isInProgress && !!endDate && dayjs().diff(endDate, 'day') <= 0
  const isMultiUserRun = userIds.length > 1

  return (
    <Modal
      escPressClose
      header={
        <div className="mr-xxs gap-x-xxs flex flex-wrap-reverse items-center justify-between text-lg sm:flex-wrap">
          <span className="truncate">{patrolRoute.name}</span>

          <div className="flex w-full justify-between sm:w-fit">
            <span className="text-md text-color-strong mr-sm shrink-0 font-medium">
              {formattedTimeframe}
            </span>

            <span className="text-base">
              {isInProgress && allVisitsComplete && (
                <Button
                  fullWidth
                  loading={isLoading}
                  loadingMessage="Completing..."
                  onClick={handleRunEnd}
                  variant="positive"
                  size="sm"
                >
                  Complete Run
                </Button>
              )}

              {isInProgress && !allVisitsComplete && (
                <Button
                  onClick={() => setIsWarningModalOpen(true)}
                  outline
                  size="sm"
                >
                  End Run
                </Button>
              )}

              {!isInProgress && allowReOpen && (
                <Button
                  fullWidth
                  loading={isLoading}
                  loadingMessage="Opening..."
                  onClick={handleReOpenRun}
                  outline
                  size="sm"
                >
                  Re Open Run
                </Button>
              )}
            </span>

            {canEditRun && (
              <Button
                variant="critical"
                className="ml-xxs"
                onClick={() => setIsDeletingModalOpen(true)}
                subtle
                size="sm"
              >
                <Icon Icon={MdDeleteForever} color="critical" size="sm" />
              </Button>
            )}
          </div>
        </div>
      }
      closeHandler={closeHandler}
    >
      <PatrolRouteRunInformation
        description={patrolRoute.description}
        endDate={endDate}
        startDate={startDate}
        startedByUserId={startedByUserId}
        userIds={userIds}
      />

      <div className="z-sticky pb-sm mt-lg gap-xs sticky top-0 flex flex-wrap">
        <div className="min-w-60 grow">
          <RunProgressBar
            showStatusLabel
            isInProgress={isInProgress}
            visitsDoneCount={visitsDoneCount}
            visitsCount={visitsCount}
          />
        </div>

        <div className="ml-auto">
          <ToggleSwitch
            label={<div className="text-sm">Hide Completed Visits</div>}
            active={isHideCompletedActive}
            onChangeValue={setIsHideCompletedActive}
          />
        </div>
      </div>

      <div className="space-y-xxs">
        {data.visits.map((runVisit, index) => {
          if (
            isHideCompletedActive &&
            runVisit.status === VisitStatus.Completed
          ) {
            return undefined
          }
          return (
            <VisitRun
              isDisabled={isLoading}
              isInteractive={isInProgress}
              isMultiUserRun={isMultiUserRun}
              onVisitRunSelect={setSelectedVisit}
              orderNumber={index + 1}
              runVisit={runVisit}
              key={runVisit.id}
            />
          )
        })}
      </div>

      {!isInProgress && !allowReOpen && (
        <div className="text-medium text-color-subtle mt-base text-center text-xs">
          Run cannot be reopened due to being closed for more than 24 hours.
        </div>
      )}

      {!!selectedVisit && (
        <PatrolRunVisitFormModal
          arrivalTime={selectedVisit.arrivalTime}
          closeHandler={() => setSelectedVisit(undefined)}
          departureTime={selectedVisit.departureTime}
          lengthMin={selectedVisit.visit.lengthMin}
          location={selectedVisit.visit.location}
          locationId={selectedVisit.visit.locationId}
          notes={selectedVisit.notes}
          onComplete={() => {
            setSelectedVisit(undefined)
            call()
          }}
          runVisitId={selectedVisit.id}
          status={selectedVisit.status}
          visitNotes={selectedVisit.visit.notes}
        />
      )}

      {isWarningModalOpen && (
        <VisitsInProgressWarningModal
          closeHandler={() => setIsWarningModalOpen(false)}
          inProgressVisits={inProgressVisits}
          patrolRouteEndHandler={handleRunEnd}
          onVisitUpdateComplete={call}
          visitsCount={visitsCount}
          visitsDoneCount={visitsDoneCount}
        />
      )}

      {isDeletingModalOpen && (
        <ActionModal
          variant="critical"
          actionText="Delete"
          icon={<Icon Icon={MdDeleteForever} color="critical" size="xxl" />}
          body={
            <div>
              <div className="text-md font-medium">
                Are you sure you want to delete this run?
              </div>
              <div>This action cannot be undone.</div>
            </div>
          }
          title="Delete Patrol Route Run"
          onCancel={() => setIsDeletingModalOpen(false)}
          onAction={handleDeleteRun}
        />
      )}
    </Modal>
  )
}

export {PatrolRouteRunModal}
